/**
 * CC / BCC watcher list and add / remove mutations for a ticket.
 *
 * Wraps:
 *   GET    /api/v1/agent/tickets/{n}/watchers
 *   POST   /api/v1/agent/tickets/{n}/cc
 *   POST   /api/v1/agent/tickets/{n}/bcc
 *   DELETE /api/v1/agent/tickets/{n}/cc/{watcher_id}
 *   DELETE /api/v1/agent/tickets/{n}/bcc/{watcher_id}
 *
 * Add / remove emit ticket_event rows server-side (cc_added,
 * cc_removed, bcc_added, bcc_removed) so the audit trail tracks the
 * change. Both mutations invalidate the watcher list query.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  WatcherAddRequest,
  WatcherKind,
  WatcherResponse,
} from "@/types/api";

export function ticketWatchersQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "watchers", ticketNumber] as const;
}

export function useTicketWatchers(
  ticketNumber: number,
  options: { enabled?: boolean } = {},
): UseQueryResult<WatcherResponse[]> {
  return useQuery({
    queryKey: ticketWatchersQueryKey(ticketNumber),
    enabled: options.enabled !== false,
    queryFn: async () => {
      const response = await api.get<WatcherResponse[]>(
        `/agent/tickets/${ticketNumber}/watchers`,
      );
      return response.data;
    },
  });
}

export interface AddWatcherArgs {
  kind: WatcherKind;
  body: WatcherAddRequest;
}

export function useAddWatcher(
  ticketNumber: number,
): UseMutationResult<WatcherResponse, Error, AddWatcherArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, body }) => {
      const response = await api.post<WatcherResponse>(
        `/agent/tickets/${ticketNumber}/${kind}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketWatchersQueryKey(ticketNumber),
      });
    },
  });
}

export interface RemoveWatcherArgs {
  kind: WatcherKind;
  watcherId: string;
}

export function useRemoveWatcher(
  ticketNumber: number,
): UseMutationResult<void, Error, RemoveWatcherArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, watcherId }) => {
      await api.delete(
        `/agent/tickets/${ticketNumber}/${kind}/${watcherId}`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketWatchersQueryKey(ticketNumber),
      });
    },
  });
}
